import { z } from "zod";
// Reuse the canonical Json type + schema from the shared barrel's first module
// so `export *` from index.ts has exactly one `Json` / `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Invoices shared contract (Strangler-Fig migration, manifest-6, slice A4).
 *
 * Single source of truth for the `invoices` wire shape shared by the web SPA
 * (`src/repositories/invoices.ts`) and the NestJS slice (`apps/api/src/invoices/*`).
 * Shape is **snake_case** so the frontend<->Nest transport is a field-for-field
 * swap (no renames), matching the Supabase generated `invoices` Row and the Nest
 * repository `.select()` lists.
 *
 * The web repo `.parse()`s Nest responses against these schemas at the flip
 * boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ MONEY-FIELD TOLERANCE (financial slice — halala-exact): PostgREST may
 * serialize `numeric` columns as a JSON number OR a string depending on the
 * column type/driver. supabase-js usually hands back `number`, but to never
 * reject a valid amount (which would silently fall back to Supabase — the #1699
 * lesson) every monetary field accepts `number | string | null`. The FE keeps
 * its Supabase-inferred `number | null` types via a cast at the call site, so no
 * consumer sees drift; parity is asserted live in the smoke (amounts equal the
 * Supabase path to the halala).
 *
 * ⚠️ RELATION TOLERANCE: the list/detail queries join different column subsets
 * of property / unit / contract / tenant / owner / recipient / items / receipts,
 * any of which may be `null` (nullable FKs). They are loose `.passthrough()`
 * records so a too-strict relation schema can't force a silent fallback. Only
 * the invoice's own identity columns are pinned.
 *
 * BE adoption note: `apps/api` does not yet import this package — it keeps its
 * class-validator DTO (already shape-aligned). Wiring Nest to consume
 * `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** A monetary value as it may arrive over the wire — number, numeric-string, or null. */
const money = z.union([z.number(), z.string()]).nullable();

/** Loose, passthrough relation shape (nullable joined object / optional). */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A persisted invoice row as the staff Finance dashboard consumes it. Own-columns
 * are pinned to the Supabase generated `invoices` Row identity; money + relation
 * fields are tolerant per the rules above. `.passthrough()` lets the server add
 * columns (new migrations) without a same-PR schema bump.
 */
export const invoiceSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    invoice_number: z.string().nullable(),
    status: z.string(),
    description: z.string().nullable().optional(),
    // Money fields (halala-exact) — tolerate number|string|null.
    subtotal: money.optional(),
    vat_rate: money.optional(),
    vat_amount: money.optional(),
    discount_amount: money.optional(),
    total_amount: money.optional(),
    paid_amount: money.optional(),
    balance: money.optional(),
    // Dates.
    invoice_date: z.string().nullable().optional(),
    due_date: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    // FK columns (nullable).
    contract_id: z.string().nullable().optional(),
    tenant_id: z.string().nullable().optional(),
    property_id: z.string().nullable().optional(),
    unit_id: z.string().nullable().optional(),
    scheduled_payment_id: z.string().nullable().optional(),
    recipient_id: z.string().nullable().optional(),
    // Structural billed-party flag — pins the DB CHECK (owner|tenant|third_party)
    // so schema drift is caught at parse time instead of landing in passthrough.
    recipient_role: z.enum(["owner", "tenant", "third_party"]).nullable().optional(),
    // Joined / enriched relations (list + detail) — loose + optional + nullable.
    recipient: relation,
    property: relation,
    unit: relation,
    contract: relation,
    tenant: relation,
    owner: relation,
    items: z.array(z.record(jsonSchema)).nullable().optional(),
    receipts: z.array(z.record(jsonSchema)).nullable().optional(),
  })
  // Tolerate extra columns the server may add without forcing a schema bump.
  .passthrough();

export type Invoice = z.infer<typeof invoiceSchema>;

/** Structural billed-party flag on `invoices.recipient_role` (DB CHECK constraint). */
export type RecipientRole = NonNullable<z.infer<typeof invoiceSchema>["recipient_role"]>;

/**
 * Finance stats projection the dashboard sums in-memory
 * (total_amount + balance + status). Mirrors `invoicesRepository.stats` /
 * `listStats`. Money fields tolerant; status an open string.
 */
export const invoiceStatsRowSchema = z
  .object({
    total_amount: money,
    balance: money,
    status: z.string().nullable(),
  })
  .passthrough();

export type InvoiceStatsRow = z.infer<typeof invoiceStatsRowSchema>;

/**
 * An open invoice + its resolved BILLED PARTY, as returned by the
 * `list_open_invoices_for_bundling` RPC and surfaced by `listOpen`. The party —
 * not recipient_id — is the master-invoice grouping key. Money fields tolerant.
 *
 * party_id/party_name are NULLABLE: the RPC now returns invoices whose billed
 * party can't be resolved (no owner on the contract/property/unit) so the FE can
 * surface them as "needs owner — fix the contract/property" instead of hiding
 * them. `party_kind` is NOT nullable — invoice_billed_party always classifies a
 * kind (e.g. an unresolvable owner-billed row is ('owner', NULL, NULL)); only the
 * id/name can be null. A null-party-id row is NOT bundleable; the create dialog
 * shows it in a non-selectable group.
 */
export const openInvoiceRowSchema = z
  .object({
    id: z.string(),
    invoice_number: z.string().nullable(),
    total_amount: money,
    balance: money,
    party_kind: z.enum(["owner", "tenant", "profile"]),
    party_id: z.string().nullable(),
    party_name: z.string().nullable(),
    type_key: z.enum(["rent", "fee", "other"]),
    // Property + unit names so the bundling picker shows which property/unit each
    // open invoice belongs to. Optional so the schema still parses against a DB
    // that predates the RPC column addition (migration applies them everywhere).
    property_name: z.string().nullable().optional(),
    unit_number: z.string().nullable().optional(),
  })
  .passthrough();

export type OpenInvoiceRow = z.infer<typeof openInvoiceRowSchema>;

/**
 * Invoice notification history row (with the inline-enriched sender name).
 * Mirrors `invoicesRepository.listNotifications`. Loose `.passthrough()` since
 * the notification columns vary and the dashboard only reads a subset.
 */
export const invoiceNotificationRowSchema = z
  .object({
    id: z.string(),
    invoice_id: z.string().nullable().optional(),
    company_id: z.string().nullable().optional(),
    sent_by: z.string().nullable().optional(),
    sent_at: z.string().nullable().optional(),
    sender: z.record(jsonSchema).nullable().optional(),
  })
  .passthrough();

export type InvoiceNotificationRow = z.infer<typeof invoiceNotificationRowSchema>;
